import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_PAGE_KEYS, getSectionProps, getVisibleSections } from "../../Common/appConfig/appConfig";
import { useAppPageConfig } from "../../Common/hooks/useAppConfig";
import {
  useApplyReviewEventV4,
  useMystudioReviewEventsV4,
  useMystudioSchedulesV4,
  useMystudioThemesV4,
} from "../../Common/api/v4";
import { MobileBottomTabBar, MobileTopHeader } from "../MobileAppShell";
import { ConfiguredPageDisabledState, ConfiguredPageIntro } from "../AppConfig/ConfiguredPageState";
import { withMobileView } from "../MobileAppShell/mobileNavigation";
import MystudioDataState from "../MystudioDataState";
import { ReviewEventApplyPage, ReviewEventCompletePage } from "./ReviewEventFlow";
import { formatScheduleDday, getScheduleDashboardSummary } from "./scheduleDashboard";
import {
  filterSchedulesByCategory,
  getScheduleFilterCount,
  getScheduleFilterFromSearch,
} from "./scheduleFilters";
import "./ShootingSchedulePage.css";

const TEXT = {
  title: "\uCD2C\uC601 \uC77C\uC815",
  prepTitle: "\uCD2C\uC601 \uC804 \uC900\uBE44\uC0AC\uD56D \uC548\uB0B4",
  prepLine1: "\uCD2C\uC601 \uB2F9\uC77C \uB354\uC6B1 \uD3B8\uD558\uACE0 \uC644\uBCBD\uD558\uAC8C \uBCF4\uB0B4\uC2E4 \uC218 \uC788\uB3C4\uB85D",
  prepLine2: "\uC900\uBE44\uC0AC\uD56D\uC744 \uC548\uB0B4\uD574\uB4DC\uB824\uC694.",
  all: "\uC804\uCCB4",
  upcoming: "\uCD2C\uC601 \uC608\uC815",
  completed: "\uCD2C\uC601 \uC644\uB8CC",
  themeRequired: "\uD14C\uB9C8\uC120\uD0DD \uD544\uC694",
  themeSelect: "\uD14C\uB9C8\uC120\uD0DD",
  themeSelectCompleteBadge: "\uD14C\uB9C8\uC120\uD0DD \uC644\uB8CC",
  notice: "\uC2A4\uD29C\uB514\uC624 \uC548\uB0B4\uC0AC\uD56D",
  noticePreview: "\uC0AC\uC804 \uC900\uBE44\uBB3C \u00B7 \uBCF5\uC7A5 \u00B7 \uBC29\uBB38 \uC2DC\uAC04",
  empty: "\uD574\uB2F9\uD558\uB294 \uCD2C\uC601 \uC77C\uC815\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.",
  originalPhotoDownload: "\uC0AC\uC9C4 \uC6D0\uBCF8 \uB2E4\uC6B4\uB85C\uB4DC",
  readySoon: "\uD654\uBA74\uC740 \uC900\uBE44 \uC911\uC785\uB2C8\uB2E4.",
  overviewAria: "\uCD2C\uC601\uC77C\uC815 \uC694\uC57D",
  overviewTitle: "\uB2E4\uAC00\uC624\uB294 \uCD2C\uC601 \uC900\uBE44",
  overviewNeedsSuffix: "\uAC1C \uD655\uC778 \uD544\uC694",
  overviewUpcoming: "\uCD2C\uC601 \uC608\uC815",
  overviewThemeNeed: "\uD14C\uB9C8\uC120\uD0DD \uD544\uC694",
  overviewCompleted: "\uCD2C\uC601 \uC644\uB8CC",
  overviewDownload: "\uB2E4\uC6B4\uB85C\uB4DC \uAC00\uB2A5",
  overviewNext: "\uAC00\uC7A5 \uAC00\uAE4C\uC6B4 \uC77C\uC815",
  overviewCurrentStep: "\uD604\uC7AC \uB2E8\uACC4",
  overviewThemeNeedMessage: "\uD14C\uB9C8 4\uAC1C \uC120\uD0DD\uC774 \uD544\uC694\uD574\uC694",
  overviewScheduleCheck: "\uC77C\uC815 \uD655\uC778\uD558\uAE30",
  reviewEventApply: "\uD6C4\uAE30\uC774\uBCA4\uD2B8 \uC2E0\uCCAD",
  reviewEventApplied: "\uD6C4\uAE30\uC774\uBCA4\uD2B8 \uC2E0\uCCAD \uC644\uB8CC",
  reviewEventApplying: "\uC2E0\uCCAD \uC911...",
  reviewEventHelp: "\uC2E0\uCCAD\uD558\uBA74 \uD574\uB2F9 \uC2A4\uD29C\uB514\uC624 \uAD00\uB9AC\uC790\uC5D0\uAC8C \uBA54\uC2DC\uC9C0\uAC00 \uC804\uB2EC\uB3FC\uC694.",
  reviewEventDelivered: "\uC2E0\uCCAD \uBA54\uC2DC\uC9C0\uB97C \uC2A4\uD29C\uB514\uC624 \uAD00\uB9AC\uC790\uC5D0\uAC8C \uC804\uB2EC\uD588\uC5B4\uC694.",
  reviewEventError: "\uD6C4\uAE30\uC774\uBCA4\uD2B8 \uC2E0\uCCAD\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4. \uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694.",
  progressAria: "\uCD2C\uC601 \uC9C4\uD589 \uC0C1\uD0DC",
  dashboardTitle: "촬영 진행 현황",
  dashboardTotal: "총 촬영",
  dashboardCompleted: "촬영 완료",
  dashboardRemaining: "남은 촬영",
  dashboardProgress: "전체 촬영 진행률",
  dashboardNext: "다음 촬영",
  dashboardAllDone: "모든 촬영을 완료했어요",
  dashboardEmpty: "등록된 촬영 일정이 없어요",
  home: "\uD648",
  scheduleTab: "\uCD2C\uC601 \uC77C\uC815",
  product: "\uC81C\uD488 \uC2E0\uCCAD",
  theme: "\uCD2C\uC601 \uD14C\uB9C8",
  mypage: "\uB9C8\uC774\uD398\uC774\uC9C0",
};

const ASSETS = {
  bell: "/static/img/mobile-header/header-bell.svg",
  prep: "/static/img/home/home-upcoming-prep-megaphone.png",
  chevronDark: "/static/img/home/home-chevron-right.svg",
  calendar: "/static/img/schedule/schedule-6998-calendar.svg",
  clock: "/static/img/schedule/schedule-6998-time.svg",
  location: "/static/img/schedule/schedule-6998-location.svg",
  camera: "/static/img/schedule/schedule-6998-camera.svg",
  chevronOrange: "/static/img/schedule/schedule-6998-chevron-orange.svg",
  chevronBlue: "/static/img/home/home-gallery-7184-3551-chevron.svg",
  download: "/static/img/home/home-recent-download.svg",
};

const FILTERS = [
  { key: "all", label: TEXT.all, nodeId: "6660:12758", textNodeId: "6660:12760" },
  { key: "upcoming", label: TEXT.upcoming, nodeId: "6660:12761", textNodeId: "6660:12763" },
  { key: "completed", label: TEXT.completed, nodeId: "6660:12755", textNodeId: "6660:12757" },
];

const SCHEDULE_PROGRESS_STEPS = [
  "\uC608\uC57D",
  "\uC900\uBE44",
  "\uCD2C\uC601",
  "\uC0AC\uC9C4",
  "\uC81C\uC791",
];

const QUERY_SCREEN_MAP = {
  "review-event-apply": "reviewEventApply",
};

function getScheduleScreenFromSearch(search) {
  const screenParam = new URLSearchParams(search).get("screen");
  return QUERY_SCREEN_MAP[screenParam] || "list";
}

function formatV4ScheduleDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const weekday = ["일", "월", "화", "수", "목", "금", "토"][date.getDay()];
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}(${weekday})`;
}

function formatV4ScheduleTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function createErpThemeLookup(themes = []) {
  return new Map(
    themes
      .filter((theme) => theme?.id)
      .map((theme) => [String(theme.id), theme]),
  );
}

export function mapV4Schedule(row, erpThemeLookup = new Map()) {
  const selectedThemeIds = Array.isArray(row.themeSelection?.selectedThemeIds)
    ? row.themeSelection.selectedThemeIds.map(String)
    : [];
  const selectedThemes = selectedThemeIds.map((id) => {
    const erpTheme = erpThemeLookup.get(id);

    return {
      id,
      title: erpTheme?.title || `ERP 테마 #${id}`,
      image: erpTheme?.thumbnailUrl || erpTheme?.images?.[0]?.url || "",
      officeName: erpTheme?.officeName || row.office?.name || "",
      category: erpTheme?.category || "",
      count: 1,
    };
  });
  const requiredThemeCount = Math.max(1, Number(row.themeSelection?.requiredCount) || 1);
  const themeSelectionComplete = Boolean(
    row.themeSelection?.locked
    || selectedThemeIds.length >= requiredThemeCount,
  );
  const reviewStatus = row.reviewEvent?.status || "not_applied";
  const isCompleted = row.status === "completed";
  const originalPhotosAvailable = row.originalPhotos?.status === "available";

  return {
    id: String(row.id),
    status: row.status,
    variant: isCompleted
      ? "completed"
      : row.themeSelection?.required && !themeSelectionComplete
        ? "theme"
        : "upcoming",
    badge: isCompleted
      ? TEXT.completed
      : row.themeSelection?.required && !themeSelectionComplete
        ? TEXT.themeRequired
        : formatScheduleDday(row.scheduledAt),
    packageName: row.productTitle || "촬영 일정",
    title: row.title || "촬영 일정",
    date: formatV4ScheduleDate(row.scheduledAt),
    rawDate: row.scheduledAt,
    time: formatV4ScheduleTime(row.scheduledAt) || "시간 미정",
    studio: row.office?.name || "스튜디오 미정",
    type: row.shootType || "촬영",
    dDay: isCompleted ? "완료" : formatScheduleDday(row.scheduledAt),
    phaseLabel: isCompleted
      ? originalPhotosAvailable ? "원본사진 제공" : "촬영 완료"
      : row.themeSelection?.required
        ? themeSelectionComplete ? TEXT.themeSelectCompleteBadge : TEXT.themeRequired
        : "촬영 준비중",
    actionLabel: isCompleted
      ? originalPhotosAvailable
        ? `원본사진 ${Number(row.originalPhotos?.count) || 0}장 다운로드 가능`
        : "원본사진 준비 중"
      : row.themeSelection?.required
        ? themeSelectionComplete ? `선택한 테마 ${selectedThemeIds.length}개` : `테마 ${requiredThemeCount}개 선택 필수`
        : "준비사항 확인",
    progressIndex: isCompleted ? 3 : themeSelectionComplete ? 2 : 1,
    thumbnail: row.thumbnailUrl || "",
    themeSelectionRequired: Boolean(row.themeSelection?.required),
    themeSelectionLocked: Boolean(row.themeSelection?.locked),
    themeSelectionComplete,
    themeRequiredTotal: requiredThemeCount,
    selectedThemeGroups: selectedThemeIds.length > 0 ? [{
      id: "selected",
      title: "ERP 촬영테마",
      count: selectedThemes.length,
      items: selectedThemes,
    }] : [],
    insight: isCompleted
      ? originalPhotosAvailable ? "원본사진을 보관 기간 안에 내려받아 주세요." : "스튜디오에서 원본사진을 준비하고 있어요."
      : row.studioNotice || "스튜디오 안내사항과 촬영 일정을 확인해 주세요.",
    studioNotice: row.studioNotice || (row.preparationItems?.length > 0)
      ? {
          summary: row.studioNotice || "촬영 전 준비사항을 확인해 주세요.",
          sections: row.preparationItems?.length > 0
            ? [{ title: "사전 준비물", items: row.preparationItems }]
            : [],
        }
      : null,
    originalPhotoArchive: row.originalPhotos ? {
      count: Number(row.originalPhotos.count) || 0,
      expiresAt: row.originalPhotos.expiresAt || "",
      expiresLabel: row.originalPhotos.expiresAt
        ? String(row.originalPhotos.expiresAt).slice(0, 10).replace(/-/g, ".")
        : "",
      downloadUrl: row.originalPhotos.downloadUrl || "",
      status: row.originalPhotos.status,
    } : null,
    reviewEvent: row.reviewEvent || null,
    reviewEventApplied: !["not_applied", "rejected", "expired"].includes(reviewStatus),
    source: "erp",
  };
}

function getThemeSelectionCount(groups = []) {
  return groups.reduce(
    (total, group) => total + group.items.reduce((groupTotal, item) => groupTotal + Math.max(1, Number(item.count) || 1), 0),
    0
  );
}

function getThemePreviewItems(groups = []) {
  return groups.flatMap((group) => group.items.flatMap((item) => {
    const count = Math.max(1, Number(item.count) || 1);

    return Array.from({ length: count }, (_, index) => ({
      id: `${group.id}-${item.id}-${index}`,
      title: item.title,
      groupTitle: group.title,
      image: item.image || "",
    }));
  }));
}

function MobileStatusBar({ compact = false }) {
  return (
    <div className={compact ? "figma-shooting-status figma-shooting-status--compact" : "figma-shooting-status"} aria-hidden="true">
      <span>9:41</span>
      <span className="figma-shooting-status__icons">
        <i />
        <i />
        <i />
      </span>
    </div>
  );
}

function ShootingHeader() {
  return (
    <header className="figma-shooting-header" data-node-id="6660:12708">
      <h1 data-node-id="6660:12709">{TEXT.title}</h1>
      <button
        className="figma-shooting-header__bell"
        type="button"
        data-node-id="6660:12710"
        aria-label="\uC54C\uB9BC"
        onClick={() => window.alert(TEXT.readySoon)}
      >
        <img src={ASSETS.bell} alt="" aria-hidden="true" />
      </button>
    </header>
  );
}


function PrepGuideCard() {
  return (
    <button
      className="figma-shooting-prep"
      type="button"
      data-node-id="6743:13264"
      onClick={() => window.alert(TEXT.prepTitle + " " + TEXT.readySoon)}
    >
      <span className="figma-shooting-prep__image" data-node-id="6743:13267" aria-hidden="true">
        <img src={ASSETS.prep} alt="" />
      </span>
      <span className="figma-shooting-prep__text" data-node-id="6743:13270">
        <strong data-node-id="6743:13271">{TEXT.prepTitle}</strong>
        <span data-node-id="6743:13272">
          {TEXT.prepLine1}
          <br />
          {TEXT.prepLine2}
        </span>
      </span>
      <img className="figma-shooting-prep__chevron" src={ASSETS.chevronDark} alt="" aria-hidden="true" />
    </button>
  );
}

export function FilterTabs({ value, onChange, counts }) {
  return (
    <div className="figma-shooting-filter" data-node-id="6660:12753">
      <div className="figma-shooting-filter__tabs" data-node-id="6660:12754" role="tablist" aria-label="촬영 일정 구분">
        {FILTERS.map((item) => {
          const selected = value === item.key;
          const count = getScheduleFilterCount(item.key, counts);

          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-label={`${item.label} ${count}건`}
              data-filter-key={item.key}
              data-node-id={item.nodeId}
              className={selected ? "figma-shooting-filter__chip figma-shooting-filter__chip--active" : "figma-shooting-filter__chip"}
              onClick={() => onChange(item.key)}
            >
              <span data-node-id={item.textNodeId}>{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DetailIcon({ type }) {
  const iconMap = {
    calendar: ASSETS.calendar,
    clock: ASSETS.clock,
    location: ASSETS.location,
    camera: ASSETS.camera,
  };

  return <img className="figma-shooting-detail__icon" src={iconMap[type]} alt="" aria-hidden="true" />;
}

function DetailRow({ icon, children }) {
  return (
    <div className="figma-shooting-detail">
      <DetailIcon type={icon} />
      <span>{children}</span>
    </div>
  );
}

function ScheduleDashboard({
  summary,
  activeFilter,
  title = TEXT.dashboardTitle,
  description = "",
  onFilterChange,
}) {
  const {
    totalCount,
    completedCount,
    remainingCount,
    completionPercent,
    nextSchedule,
    nextDateLabel,
    nextDday,
  } = summary;
  const progressMax = Math.max(totalCount, 1);
  const progressLabel = `전체 촬영 ${totalCount}건 중 ${completedCount}건 완료`;

  return (
    <section className="figma-shooting-dashboard" aria-labelledby="figma-shooting-dashboard-title">
      <div className="figma-shooting-dashboard__header">
        <span>
          <h2 id="figma-shooting-dashboard-title">{title}</h2>
          {description ? <small>{description}</small> : null}
        </span>
        <strong>{completionPercent}% 완료</strong>
      </div>

      <div className="figma-shooting-dashboard__body">
        <div className="figma-shooting-dashboard__summary">
          <div className="figma-shooting-dashboard__metrics" role="group" aria-label="촬영 건수 요약">
            <div className="figma-shooting-dashboard__metric">
              <span>{TEXT.dashboardTotal}</span>
              <strong>{totalCount}<small>건</small></strong>
            </div>
            <button
              type="button"
              className={`figma-shooting-dashboard__metric figma-shooting-dashboard__metric-button${activeFilter === "completed" ? " is-active" : ""}`}
              aria-pressed={activeFilter === "completed"}
              aria-label={`촬영 완료 ${completedCount}건 보기`}
              onClick={() => onFilterChange("completed")}
            >
              <span>{TEXT.dashboardCompleted}</span>
              <strong>{completedCount}<small>건</small></strong>
            </button>
            <button
              type="button"
              className={`figma-shooting-dashboard__metric figma-shooting-dashboard__metric-button${activeFilter === "upcoming" ? " is-active" : ""}`}
              aria-pressed={activeFilter === "upcoming"}
              aria-label={`남은 촬영 ${remainingCount}건 보기`}
              onClick={() => onFilterChange("upcoming")}
            >
              <span>{TEXT.dashboardRemaining}</span>
              <strong>{remainingCount}<small>건</small></strong>
            </button>
          </div>

          <div className="figma-shooting-dashboard__progress-copy">
            <span>{TEXT.dashboardProgress}</span>
            <strong>{completionPercent}%</strong>
          </div>
          <progress
            className="figma-shooting-dashboard__progress"
            value={completedCount}
            max={progressMax}
            aria-label={progressLabel}
          />
        </div>

        {nextSchedule ? (
          <button
            type="button"
            className="figma-shooting-dashboard__next"
            aria-label={`${TEXT.dashboardNext} ${nextDday}, ${nextDateLabel} ${nextSchedule.title} 보기`}
            onClick={() => onFilterChange("upcoming")}
          >
            <span className="figma-shooting-dashboard__next-badge">{nextDday}</span>
            <span className="figma-shooting-dashboard__next-copy">
              <small>{TEXT.dashboardNext} · {nextDday}</small>
              <strong>{nextDateLabel} · {nextSchedule.title}</strong>
            </span>
            <span className="figma-shooting-dashboard__next-chevron" aria-hidden="true">›</span>
          </button>
        ) : (
          <div className="figma-shooting-dashboard__next figma-shooting-dashboard__next--empty">
            <span className="figma-shooting-dashboard__next-copy">
              <small>{TEXT.dashboardNext}</small>
              <strong>{totalCount > 0 ? TEXT.dashboardAllDone : TEXT.dashboardEmpty}</strong>
            </span>
          </div>
        )}
      </div>
    </section>
  );
}

function ScheduleOverviewProgress({ currentIndex = 0 }) {
  return (
    <div className="figma-shooting-overview__progress" aria-label={TEXT.progressAria}>
      {SCHEDULE_PROGRESS_STEPS.map((step, index) => {
        const state = index < currentIndex ? "done" : index === currentIndex ? "active" : "pending";

        return (
          <span key={step} className={`figma-shooting-overview__progress-step figma-shooting-overview__progress-step--${state}`}>
            <i aria-hidden="true" />
            <b>{step}</b>
            {state === "active" && <em>{TEXT.overviewCurrentStep}</em>}
          </span>
        );
      })}
    </div>
  );
}

function ScheduleOverview({ schedules, onThemeSelect, onShowUpcoming }) {
  const nextSchedule = schedules.find((schedule) => schedule.status === "upcoming") || schedules[0];
  const isThemeRequired = nextSchedule?.variant === "theme";
  const actionMessage = isThemeRequired ? TEXT.overviewThemeNeedMessage : nextSchedule?.actionLabel || nextSchedule?.phaseLabel;
  const ctaLabel = isThemeRequired ? TEXT.themeSelect : TEXT.overviewScheduleCheck;
  const handleOverviewAction = isThemeRequired ? onThemeSelect : onShowUpcoming;

  if (!nextSchedule) {
    return null;
  }

  return (
    <section className="figma-shooting-overview" aria-label={TEXT.overviewAria}>
      <div className="figma-shooting-overview__headline">
        <span>{TEXT.overviewTitle}</span>
        <strong>{nextSchedule.dDay || nextSchedule.badge}</strong>
      </div>
      <div className="figma-shooting-overview__schedule">
        <p className="figma-shooting-overview__package">{nextSchedule.packageName}</p>
        <h2>{nextSchedule.title}</h2>
        <div className="figma-shooting-overview__meta">
          <DetailRow icon="calendar">{nextSchedule.date}</DetailRow>
          <DetailRow icon="clock">{nextSchedule.time}</DetailRow>
          <DetailRow icon="location">{nextSchedule.studio}</DetailRow>
        </div>
      </div>
      <ScheduleOverviewProgress currentIndex={nextSchedule.progressIndex} />

      <div className="figma-shooting-overview__action">
        <span>{actionMessage}</span>
        <button type="button" onClick={handleOverviewAction}>
          {ctaLabel}
        </button>
      </div>
      {nextSchedule && (
        <div className="figma-shooting-overview__next">
          <span>{nextSchedule.dDay || nextSchedule.badge}</span>
          <p>
            <b>{nextSchedule.title}</b>
            <small>{TEXT.overviewNext} · {nextSchedule.actionLabel || nextSchedule.phaseLabel}</small>
          </p>
        </div>
      )}
    </section>
  );
}

function ScheduleDateHeader({ date }) {
  return (
    <div className="figma-shooting-date-header">
      <span>{date}</span>
    </div>
  );
}

function ScheduleBadge({ schedule }) {
  return (
    <span className={"figma-shooting-badge figma-shooting-badge--" + schedule.variant}>
      {schedule.badge}
    </span>
  );
}

function ScheduleCardVisual({ schedule }) {
  const selectedThemePreviews = getThemePreviewItems(schedule.selectedThemeGroups);
  const selectedTheme = selectedThemePreviews[0];
  const selectedThemeCount = selectedThemePreviews.length;
  const requiredThemeCount = schedule.themeRequiredTotal || 1;

  if (schedule.themeSelectionRequired) {
    if (selectedTheme && selectedThemeCount >= requiredThemeCount) {
      return (
        <div
          className="figma-shooting-card__visual figma-shooting-card__visual--theme is-selected"
          role="img"
          aria-label={`${selectedTheme.title} 등 선택한 테마 ${selectedThemeCount}개`}
        >
          {selectedTheme.image || schedule.thumbnail ? (
            <img
              src={selectedTheme.image || schedule.thumbnail}
              alt=""
              onError={(event) => {
                event.currentTarget.hidden = true;
              }}
            />
          ) : (
            <span className="figma-shooting-card__image-empty" aria-hidden="true">선택한 테마</span>
          )}
          <strong>{schedule.dDay || schedule.badge}</strong>
          {selectedThemeCount > 1 && <span className="figma-shooting-card__theme-count">+{selectedThemeCount - 1}</span>}
          <div className="figma-shooting-card__visual-caption">
            <b>{selectedTheme.title}</b>
            <small>{selectedTheme.groupTitle} · 선택한 테마</small>
          </div>
        </div>
      );
    }

    return (
      <div
        className="figma-shooting-card__visual figma-shooting-card__visual--theme is-pending"
        role="img"
        aria-label="테마 선택 전 미리보기"
      >
        {schedule.thumbnail ? (
          <img
            className="figma-shooting-card__pending-image"
            src={schedule.thumbnail}
            alt=""
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="figma-shooting-card__image-empty" aria-hidden="true">테마 선택 전</span>
        )}
        <strong>{schedule.dDay || schedule.badge}</strong>
        <div className="figma-shooting-card__visual-caption">
          <b>테마 선택 전</b>
          <small>원하는 분위기를 골라주세요</small>
        </div>
      </div>
    );
  }

  return (
    <div className="figma-shooting-card__visual" role="img" aria-label={`${schedule.title} 촬영 이미지`}>
      {schedule.thumbnail ? (
        <img
          src={schedule.thumbnail}
          alt=""
          loading="lazy"
          decoding="async"
          onError={(event) => {
            event.currentTarget.hidden = true;
          }}
        />
      ) : (
        <span className="figma-shooting-card__image-empty" aria-hidden="true">이미지 준비 중</span>
      )}
      <strong>{schedule.dDay || schedule.badge}</strong>
    </div>
  );
}

function ScheduleCard({ schedule, onDownloadOriginal, onThemeSelect, onReviewEvent, reviewEventApplied, reviewEventPending }) {
  const [noticeOpen, setNoticeOpen] = useState(false);
  const noticePanelId = `studio-notice-${schedule.id}`;
  const selectedThemeCount = getThemeSelectionCount(schedule.selectedThemeGroups);

  return (
    <article className={"figma-shooting-card figma-shooting-card--" + schedule.variant} data-node-id={schedule.nodeId}>
      <div className="figma-shooting-card__status-row">
        <ScheduleBadge schedule={schedule} />
        <span>{schedule.phaseLabel}</span>
      </div>

      <div className="figma-shooting-card__main">
        <div className="figma-shooting-card__body">
          <p className="figma-shooting-card__package">{schedule.packageName}</p>
          <h2 className="figma-shooting-card__title">{schedule.title}</h2>
          <p className="figma-shooting-card__action">{schedule.actionLabel}</p>
        </div>

        <ScheduleCardVisual schedule={schedule} />
      </div>

      <div className="figma-shooting-card__details">
        <DetailRow icon="calendar">{schedule.date}</DetailRow>
        <DetailRow icon="clock">{schedule.time}</DetailRow>
        <DetailRow icon="location">{schedule.studio}</DetailRow>
        <DetailRow icon="camera">{schedule.type}</DetailRow>
      </div>

      {schedule.selectedThemeGroups?.length > 0 && (
        <div className="figma-shooting-selected-themes">
          <div className="figma-shooting-selected-themes__header">
            <strong>선택한 테마</strong>
            <span>총 {getThemeSelectionCount(schedule.selectedThemeGroups)}개</span>
          </div>
          {schedule.selectedThemeGroups.map((group) => (
            <p key={group.id}>
              <strong>{group.title}</strong>
              <span>{group.items.map((item) => `${item.title} ${item.count}\uAC1C`).join(", ")}</span>
            </p>
          ))}
        </div>
      )}

      {schedule.themeSelectionRequired && !schedule.themeSelectionComplete && (
        <button className="figma-shooting-theme-button" type="button" data-node-id="6998:12220" onClick={() => onThemeSelect(schedule.id)}>
          <span>{selectedThemeCount > 0 ? "테마선택 계속하기" : TEXT.themeSelect}</span>
          <img src={ASSETS.chevronOrange} alt="" aria-hidden="true" />
        </button>
      )}

      {schedule.status === "upcoming" && schedule.studioNotice && (
        <div className="figma-shooting-notice-wrap">
          <button
            className={noticeOpen ? "figma-shooting-notice figma-shooting-notice--open" : "figma-shooting-notice"}
            type="button"
            data-node-id="6998:12112"
            aria-expanded={noticeOpen}
            aria-controls={noticePanelId}
            aria-label={`${schedule.title} 스튜디오 안내사항 ${noticeOpen ? "접기" : "보기"}`}
            onClick={() => setNoticeOpen((open) => !open)}
          >
            <span className="figma-shooting-notice__label">
              <strong>{TEXT.notice}</strong>
              <small>{TEXT.noticePreview}</small>
            </span>
            <img src={ASSETS.chevronBlue} alt="" aria-hidden="true" />
          </button>
          {noticeOpen && (
            <section
              className="figma-shooting-notice-panel"
              id={noticePanelId}
              aria-label={`${schedule.title} 스튜디오 안내사항`}
            >
              <div className="figma-shooting-notice-panel__heading">
                <span>촬영별 안내</span>
                <strong>{schedule.studio}</strong>
                <small>{schedule.date}</small>
              </div>
              <p className="figma-shooting-notice-panel__summary">{schedule.studioNotice.summary}</p>
              <div className="figma-shooting-notice-panel__sections">
                {schedule.studioNotice.sections.map((section) => (
                  <section key={section.title} className="figma-shooting-notice-panel__section">
                    <h3>{section.title}</h3>
                    <ul>
                      {section.items.map((item) => <li key={item}>{item}</li>)}
                    </ul>
                  </section>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      {schedule.reviewEvent && (
      <div className={reviewEventApplied ? "figma-shooting-review-event is-applied" : "figma-shooting-review-event"}>
        <button
          type="button"
          disabled={reviewEventApplied || reviewEventPending}
          onClick={() => onReviewEvent(schedule)}
          aria-label={`${schedule.title} 후기이벤트 신청`}
        >
          {reviewEventPending ? TEXT.reviewEventApplying : reviewEventApplied ? TEXT.reviewEventApplied : TEXT.reviewEventApply}
        </button>
        <small>{reviewEventApplied ? TEXT.reviewEventDelivered : TEXT.reviewEventHelp}</small>
      </div>
      )}

      {schedule.variant === "completed" && schedule.originalPhotoArchive?.status === "available" && (
        <div className="figma-shooting-completed-actions">
          <button
            className="figma-shooting-download-cta"
            type="button"
            onClick={onDownloadOriginal}
            data-node-id="6998:12702"
          >
            <img src={ASSETS.download} alt="" aria-hidden="true" />
            <span>{TEXT.originalPhotoDownload}</span>
          </button>
        </div>
      )}
    </article>
  );
}

export default function FigmaShootingSchedulePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const handleTabNavigate = (target) => navigate(withMobileView(target));
  const locationSearch = location.search;
  const initialParams = new URLSearchParams(locationSearch);
  const initialScreen = getScheduleScreenFromSearch(locationSearch);
  const [filter, setFilter] = useState(() => getScheduleFilterFromSearch(locationSearch));
  const [screen, setScreen] = useState(initialScreen);
  const [reviewEventAppliedIds, setReviewEventAppliedIds] = useState([]);
  const [reviewEventPendingId, setReviewEventPendingId] = useState("");
  const [reviewEventScheduleId, setReviewEventScheduleId] = useState(() => initialParams.get("reviewSchedule") || "");
  const [reviewEventErrorMessage, setReviewEventErrorMessage] = useState("");
  const [reviewTermsAccepted, setReviewTermsAccepted] = useState(false);
  const [successfulReviewEventScheduleId, setSuccessfulReviewEventScheduleId] = useState("");
  const { pageConfig: schedulePageConfig } = useAppPageConfig(APP_PAGE_KEYS.schedules);
  const scheduleQuery = useMystudioSchedulesV4();
  const themeQuery = useMystudioThemesV4();
  const reviewEventCatalogQuery = useMystudioReviewEventsV4();
  const reviewEventMutation = useApplyReviewEventV4();
  const v4ScheduleRows = Array.isArray(scheduleQuery.data?.data?.items)
    ? scheduleQuery.data.data.items
    : [];
  const erpThemeRows = Array.isArray(themeQuery.data?.data?.items)
    ? themeQuery.data.data.items
    : [];
  const erpThemeLookup = useMemo(
    () => createErpThemeLookup(erpThemeRows),
    [erpThemeRows],
  );
  const baseScheduleSource = useMemo(() => {
    if (v4ScheduleRows.length > 0) {
      return v4ScheduleRows
        .filter((row) => row.status !== "cancelled")
        .map((row) => mapV4Schedule(row, erpThemeLookup));
    }

    return [];
  }, [
    erpThemeLookup,
    v4ScheduleRows,
  ]);
  const scheduleSource = useMemo(() => baseScheduleSource.map((schedule) => {
    const dynamicDday = schedule.status === "upcoming" ? formatScheduleDday(schedule.date) : schedule.dDay;
    const normalizedSchedule = schedule.status === "upcoming"
      ? {
          ...schedule,
          dDay: dynamicDday,
          badge: /^D-/.test(schedule.badge || "") ? dynamicDday : schedule.badge,
        }
      : schedule;

    if (
      normalizedSchedule.status === "upcoming"
      && normalizedSchedule.themeSelectionRequired
      && normalizedSchedule.themeSelectionComplete
    ) {
      return {
        ...normalizedSchedule,
        variant: "upcoming",
        badge: TEXT.themeSelectCompleteBadge,
        phaseLabel: "촬영 준비 완료",
        progressIndex: 2,
      };
    }

    return normalizedSchedule;
  }), [baseScheduleSource]);
  const reviewEventSchedule = useMemo(() => {
    const selectedSchedule = scheduleSource.find(
      (schedule) => String(schedule.id) === String(reviewEventScheduleId),
    );
    return selectedSchedule;
  }, [reviewEventScheduleId, scheduleSource]);
  const reviewEventCatalog = Array.isArray(reviewEventCatalogQuery.data?.data?.items)
    ? reviewEventCatalogQuery.data.data.items
    : [];
  const selectedReviewEvent = useMemo(() => {
    const reviewEventId = String(reviewEventSchedule?.reviewEvent?.id || "");
    if (!reviewEventId) return undefined;
    return reviewEventCatalog.find((event) => String(event.id) === reviewEventId);
  }, [reviewEventCatalog, reviewEventSchedule]);
  const scheduleDashboard = useMemo(() => getScheduleDashboardSummary(scheduleSource), [scheduleSource]);
  const schedules = useMemo(() => {
    return filterSchedulesByCategory(scheduleSource, filter);
  }, [filter, scheduleSource]);
  const visibleScheduleSections = useMemo(
    () => getVisibleSections(schedulePageConfig),
    [schedulePageConfig],
  );
  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    const nextScreen = getScheduleScreenFromSearch(locationSearch);
    const nextFilter = getScheduleFilterFromSearch(locationSearch);
    const nextReviewScheduleId = params.get("reviewSchedule") || "";

    setFilter((currentFilter) => (currentFilter === nextFilter ? currentFilter : nextFilter));
    setReviewEventScheduleId((currentId) => currentId === nextReviewScheduleId ? currentId : nextReviewScheduleId);

    setScreen((currentScreen) => {
      if (nextScreen !== "list") return nextScreen;
      return ["reviewEventApply", "reviewEventComplete"].includes(currentScreen) ? "list" : currentScreen;
    });
  }, [locationSearch]);

  const handleFilterChange = (nextFilter) => {
    if (!FILTERS.some((item) => item.key === nextFilter)) return;

    setFilter(nextFilter);
    const params = new URLSearchParams(locationSearch);
    params.set("category", nextFilter);
    const nextSearch = params.toString();
    navigate(`/tabs/schedules${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
  };

  const returnToScheduleList = () => {
    setScreen("list");

    const params = new URLSearchParams(locationSearch);
    params.delete("screen");
    params.delete("refresh");
    params.delete("reviewSchedule");
    const nextSearch = params.toString();
    setReviewEventScheduleId("");
    setReviewEventErrorMessage("");
    setReviewTermsAccepted(false);
    setSuccessfulReviewEventScheduleId("");
    navigate(`/tabs/schedules${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
  };

  const openDownloadScreen = (selectedSchedule) => {
    const scheduleQueryValue = selectedSchedule?.id
      ? `?scheduleId=${encodeURIComponent(selectedSchedule.id)}`
      : "";
    navigate(`/mypage/photos${scheduleQueryValue}`);
  };

  const openThemeSelection = (scheduleId) => {
    const scheduleItem = scheduleSource.find((item) => String(item.id) === String(scheduleId));
    const params = new URLSearchParams();
    params.set("selectSchedule", scheduleId);
    params.set("category", "upcoming");
    if (scheduleItem?.themeRequiredTotal) {
      params.set("requiredThemeCount", String(scheduleItem.themeRequiredTotal));
    }
    navigate(`/tabs/themes?${params.toString()}`);
  };

  const openReviewEventApplication = (schedule) => {
    const scheduleId = String(schedule.id);
    if (reviewEventPendingId || reviewEventAppliedIds.includes(scheduleId)) return;

    setReviewEventScheduleId(scheduleId);
    setReviewEventErrorMessage("");
    setReviewTermsAccepted(false);
    setSuccessfulReviewEventScheduleId("");
    setScreen("reviewEventApply");

    const params = new URLSearchParams(locationSearch);
    params.set("screen", "review-event-apply");
    params.set("reviewSchedule", scheduleId);
    navigate(`/tabs/schedules?${params.toString()}`, { replace: false });
  };

  const submitSelectedReviewEvent = async () => {
    if (!reviewEventSchedule) return;

    const scheduleId = String(reviewEventSchedule.id);
    if (reviewEventPendingId || reviewEventAppliedIds.includes(scheduleId)) return;

    setReviewEventPendingId(scheduleId);
    setReviewEventErrorMessage("");

    const showReviewEventComplete = () => {
      setReviewEventAppliedIds((currentIds) => (
        currentIds.includes(scheduleId) ? currentIds : [...currentIds, scheduleId]
      ));
      setSuccessfulReviewEventScheduleId(scheduleId);
      setScreen("reviewEventComplete");

    };

    try {
      const reviewEventId = reviewEventSchedule.reviewEvent?.id;
      if (!reviewEventId) {
        throw new Error("후기이벤트 신청 정보가 없습니다.");
      }
      if (!selectedReviewEvent) {
        throw new Error("신청 가능한 후기 이벤트 정보를 확인할 수 없습니다.");
      }
      if (!selectedReviewEvent.eligibility?.eligible) {
        throw new Error(selectedReviewEvent.eligibility?.reasonCode || "현재 신청할 수 없는 후기 이벤트입니다.");
      }
      if (selectedReviewEvent.consent?.required && !reviewTermsAccepted) {
        throw new Error("필수 약관에 동의해 주세요.");
      }
      const termsVersionId = selectedReviewEvent.terms?.id
        ? Number(selectedReviewEvent.terms.id)
        : undefined;
      if (selectedReviewEvent.terms?.id && !Number.isFinite(termsVersionId)) {
        throw new Error("후기 이벤트 약관 버전 정보를 확인할 수 없습니다.");
      }
      await reviewEventMutation.mutateAsync({
        reviewEventId: String(selectedReviewEvent.id),
        input: {
          scheduleId: Number(reviewEventSchedule.id),
          termsVersionId,
          acceptedTerms: selectedReviewEvent.consent?.required
            ? reviewTermsAccepted
            : false,
        },
      });
      showReviewEventComplete();
    } catch (error) {
      console.warn("Review event application failed.", error);
      setReviewEventErrorMessage(error?.message || TEXT.reviewEventError);
    } finally {
      setReviewEventPendingId("");
    }
  };

  if (schedulePageConfig.page.enabled === false) {
    return (
      <ConfiguredPageDisabledState
        pageConfig={schedulePageConfig}
        activePath="/tabs/schedules"
        onNavigate={handleTabNavigate}
      />
    );
  }

  if (screen === "reviewEventApply") {
    const catalogUnavailable = (
      reviewEventCatalogQuery.isLoading
      || Boolean(reviewEventCatalogQuery.error)
      || reviewEventCatalogQuery.data?.data?.availability !== "available"
      || !selectedReviewEvent
      || !selectedReviewEvent.eligibility?.eligible
    );
    const unavailableMessage = reviewEventCatalogQuery.isLoading
      ? "후기 이벤트 신청 정보를 확인하고 있습니다."
      : reviewEventCatalogQuery.error
        ? "후기 이벤트 신청 정보를 불러오지 못했습니다."
        : selectedReviewEvent?.eligibility?.reasonCode
          || reviewEventCatalogQuery.data?.data?.reasonCode
          || "현재 신청할 수 있는 후기 이벤트가 없습니다.";
    return (
      <ReviewEventApplyPage
        onBack={returnToScheduleList}
        onApply={submitSelectedReviewEvent}
        pending={reviewEventPendingId === String(reviewEventSchedule?.id || "")}
        errorMessage={reviewEventErrorMessage}
        event={selectedReviewEvent}
        acceptedTerms={reviewTermsAccepted}
        onAcceptedTermsChange={setReviewTermsAccepted}
        available={!catalogUnavailable}
        unavailableMessage={catalogUnavailable ? unavailableMessage : ""}
      />
    );
  }

  if (
    screen === "reviewEventComplete"
    && successfulReviewEventScheduleId
    && successfulReviewEventScheduleId === String(reviewEventSchedule?.id || "")
  ) {
    return (
      <ReviewEventCompletePage
        schedule={reviewEventSchedule}
        event={selectedReviewEvent}
        onBack={returnToScheduleList}
        onConfirm={returnToScheduleList}
      />
    );
  }

  const renderScheduleSection = (section) => {
    const sectionProps = getSectionProps(section);

    if (section.key === "dashboard") {
      return (
        <ScheduleDashboard
          key={section.key}
          {...sectionProps}
          summary={scheduleDashboard}
          activeFilter={filter}
          onFilterChange={handleFilterChange}
        />
      );
    }

    if (section.key === "filters") {
      return (
        <section className="figma-shooting-config-section figma-shooting-config-section--filters" key={section.key}>
          {sectionProps.title ? <h2>{sectionProps.title}</h2> : null}
          {sectionProps.description ? <p>{sectionProps.description}</p> : null}
          <FilterTabs value={filter} onChange={handleFilterChange} counts={scheduleDashboard} />
        </section>
      );
    }

    if (section.key === "schedule_list") {
      return (
        <section className="figma-shooting-config-section figma-shooting-config-section--list" key={section.key}>
          {sectionProps.title ? <h2>{sectionProps.title}</h2> : null}
          {sectionProps.description ? <p>{sectionProps.description}</p> : null}
          {scheduleQuery.configured && scheduleQuery.isLoading ? (
            <MystudioDataState type="loading" />
          ) : !scheduleQuery.configured || scheduleQuery.error ? (
            <MystudioDataState
              type="error"
              error={scheduleQuery.error}
              title={!scheduleQuery.configured ? "촬영일정 API가 연결되지 않았어요" : undefined}
              description={!scheduleQuery.configured ? "MyStudio API 연결 주소를 확인해 주세요." : undefined}
              onRetry={scheduleQuery.configured ? scheduleQuery.refetch : undefined}
            />
          ) : scheduleQuery.data?.data?.availability === "unavailable" ? (
            <MystudioDataState
              type="empty"
              title="촬영일정 연동을 준비하고 있어요"
              description={scheduleQuery.data?.data?.reasonCode || "스튜디오에서 일정을 연동하면 이곳에 표시됩니다."}
            />
          ) : schedules.length > 0 ? (
            schedules.map((schedule, index) => {
              const previous = schedules[index - 1];
              const showDateHeader = !previous || previous.date !== schedule.date;

              return (
                <div className="figma-shooting-list-item" key={schedule.id}>
                  {showDateHeader && <ScheduleDateHeader date={schedule.date} />}
                  <ScheduleCard
                    schedule={schedule}
                    onDownloadOriginal={() => openDownloadScreen(schedule)}
                    onThemeSelect={openThemeSelection}
                    onReviewEvent={openReviewEventApplication}
                    reviewEventApplied={schedule.reviewEventApplied || reviewEventAppliedIds.includes(String(schedule.id))}
                    reviewEventPending={reviewEventPendingId === String(schedule.id)}
                  />
                </div>
              );
            })
          ) : (
            <MystudioDataState
              type="empty"
              title={TEXT.empty}
              description="스튜디오에서 촬영일정을 등록하면 이곳에 표시됩니다."
            />
          )}
        </section>
      );
    }

    return null;
  };

  return (
    <section className="figma-shooting-page mobile-app-frame" data-node-id="6660:shooting-schedule">
      <MobileStatusBar />
      <MobileTopHeader />
      <main className="figma-shooting-scroll">
        <ConfiguredPageIntro pageConfig={schedulePageConfig} />
        <div className="figma-shooting-content" data-node-id="6660:12747">
          {visibleScheduleSections.map(renderScheduleSection)}
        </div>
      </main>
      <MobileBottomTabBar activePath={window.location.pathname} onNavigate={handleTabNavigate} />
    </section>
  );
}
